import { FC, useMemo } from 'react';
import { RadixTable, TColumn } from '@features/ui';
import { TPayoutsAmountReportTable } from '@src/types';
import { formatCurrency } from '@lib/utils.ts';
import type { TPayoutsByMonthRes } from '@src/types/reports';

interface PayoutsAmountReportTableProps {
  data?: TPayoutsByMonthRes[];
}

// Helper function to map API response to table data format
const mapApiDataToTableFormat = (apiData: TPayoutsByMonthRes[]): TPayoutsAmountReportTable[] => {
  return apiData.map((item) => ({
    month: item.month,
    amount: parseFloat(item.amount),
  }));
};

// Helper function to compute totals row
const computeTotalsRow = (data: TPayoutsAmountReportTable[]): TPayoutsAmountReportTable | null => {
  if (data.length === 0) return null;

  return {
    month: 'TOTALS',
    amount: data.reduce((sum, row) => sum + (row.amount || 0), 0),
  };
};

export const PayoutsAmountReportTable: FC<PayoutsAmountReportTableProps> = ({ data = [] }) => {
  const tableData = useMemo(() => {
    const mapped = mapApiDataToTableFormat(data);
    const totals = computeTotalsRow(mapped);
    return totals ? [...mapped, totals] : mapped;
  }, [data]);

  const columns = useMemo(
    (): TColumn<TPayoutsAmountReportTable>[] => [
      {
        title: 'Month',
        render: (item, index) => {
          const isLast = tableData.length === (index ?? 0) + 1;

          return (
            <p className={isLast ? ' font-bold' : ''}>{item?.month ?? '-'}</p>
          );
        },
      },
      {
        title: `Amount $`,
        render: (item, index) => {
          const isLast = tableData.length === (index ?? 0) + 1;

          return (
            <p className={isLast ? ' font-bold' : ''}>
              ${formatCurrency(item?.amount ?? 0)}
            </p>
          );
        },
      },
    ],
    [tableData]
  );

  return <RadixTable columns={columns} data={tableData} />;
};
